import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useState, useMemo, ReactNode } from "react";
import { Button, HStack } from "@chakra-ui/react-v3";
import type { ColumnDef } from "../../components/base-table-layout";
import { BaseTableLayout } from "../../components/base-table-layout";
import { TableRow } from "../../utils/tables-metadata";
import { getLabPanelRows, LabPanelRowData, LabRowData } from "./lab-rows";

export type PanelLabTableProps = {
  fhirSdk: FhirBundleSdk;
  onRowClick: (row: TableRow) => void;
  filterQuery: string;
  displayName?: string;
  isRowViewed?: (row: TableRow) => boolean;
  headerActionsSlot?: ReactNode;
};

export function PanelLabTable({
  fhirSdk,
  onRowClick,
  filterQuery,
  displayName = "Labs",
  isRowViewed,
  headerActionsSlot,
}: PanelLabTableProps) {
  const [selectedPanelId, setSelectedPanelId] = useState<string | undefined>(undefined);

  const { panelRows, drillDownData } = useMemo(() => {
    return getLabPanelRows({ bundle: fhirSdk });
  }, [fhirSdk]);

  const panelColumns: ColumnDef<LabPanelRowData>[] = useMemo(
    () => [
      { key: "name", header: "Panel Name", maxWidth: "400px" },
      { key: "date", header: "Date", maxWidth: "150px", sort: "desc" },
      { key: "labCount", header: "Lab Count", maxWidth: "100px" },
    ],
    []
  );

  const drillDownColumns: ColumnDef<LabRowData>[] = useMemo(
    () => [
      { key: "observation", header: "Observation", maxWidth: "300px" },
      { key: "value", header: "Value" },
      { key: "interpretation", header: "Interpretation" },
      { key: "referenceRange", header: "Reference Range" },
      { key: "date", header: "Date", maxWidth: "150px", sort: "desc" },
    ],
    []
  );

  const isInDrillDown = Boolean(selectedPanelId);
  const drillDownRows = selectedPanelId ? drillDownData.get(selectedPanelId) ?? [] : [];

  const backButton = isInDrillDown && (
    <Button size="sm" variant="outline" onClick={() => setSelectedPanelId(undefined)}>
      ← Back to Panels
    </Button>
  );

  function handleRowClick(row: TableRow) {
    if (!isInDrillDown) {
      // In panel list view, clicking drills down
      setSelectedPanelId(row.id);
      return;
    }
    // In drill-down view, open side panel with focusedObservationId preserved
    onRowClick(row);
  }

  function getDrillDownRowStyle(row: LabRowData) {
    return {
      ...(row.backgroundColor ? { bg: row.backgroundColor } : {}),
      ...(row.hoverColor ? { _hover: { bg: row.hoverColor } } : {}),
    };
  }

  if (isInDrillDown) {
    return (
      <BaseTableLayout
        title={displayName}
        rows={drillDownRows as { id: string }[]}
        columns={drillDownColumns as ColumnDef<{ id: string }>[]}
        filterQuery={filterQuery}
        onRowClick={handleRowClick}
        isRowViewed={isRowViewed}
        headerActionsSlot={
          <HStack gap={2}>
            {backButton}
            {headerActionsSlot}
          </HStack>
        }
        getRowStyle={row => getDrillDownRowStyle(row as unknown as LabRowData)}
      />
    );
  }

  return (
    <BaseTableLayout
      title={displayName}
      rows={panelRows as { id: string }[]}
      columns={panelColumns as ColumnDef<{ id: string }>[]}
      filterQuery={filterQuery}
      onRowClick={handleRowClick}
      isRowViewed={isRowViewed}
      headerActionsSlot={headerActionsSlot}
    />
  );
}
